/**
 * Telemetry Service
 *
 * Tracks user events and behavior for analytics and product improvement.
 * Respects privacy settings and provides opt-out capability.
 *
 * Features: batched flushing, funnel tracking, engagement scoring,
 * sampling, offline persistence, session replays markers, throttling.
 */

const TELEMETRY_ENABLED = import.meta.env.VITE_TELEMETRY_ENABLED !== 'false';
const TELEMETRY_ENDPOINT = import.meta.env.VITE_TELEMETRY_ENDPOINT || '/api/telemetry/events';
const BATCH_SIZE = 10;
const FLUSH_INTERVAL = 30000; // 30 seconds
const MAX_QUEUE_SIZE = 200; // hard cap to prevent memory issues
const OFFLINE_STORAGE_KEY = 'gabhan_telemetry_offline';

// Event queue for batching
let eventQueue = [];
let flushTimeout = null;
let _flushRetryCount = 0;
const MAX_FLUSH_RETRIES = 3;

// Session tracking
let sessionId = null;
let sessionStart = null;

// Throttle tracking: { eventName → { count, windowStart } }
const _throttleMap = new Map();
const THROTTLE_WINDOW = 10_000; // 10s
const THROTTLE_MAX = 20; // max events per type per window

// Sampling rates per event name (0-1 range, 1 = send all)
const SAMPLING_RATES = {
  page_view: 1,
  feature_usage: 1,
  conversion: 1,
  timing: 0.5, // sample 50% of timing events
  error: 1,
  identify: 1,
  search: 0.5,
  _default: 1,
};

// ─── Engagement tracking ────────────────────────────────────────

let _engagementState = {
  clicks: 0,
  scrollDepth: 0,
  keystrokes: 0,
  activeSeconds: 0,
  _activeSince: null,
  _activityTimer: null,
};

/**
 * Initialize engagement listeners (call once)
 */
const _initEngagement = () => {
  if (typeof window === 'undefined') return;
  const onActive = () => {
    if (!_engagementState._activeSince) {
      _engagementState._activeSince = Date.now();
    }
  };
  const onIdle = () => {
    if (_engagementState._activeSince) {
      _engagementState.activeSeconds += Math.round(
        (Date.now() - _engagementState._activeSince) / 1000
      );
      _engagementState._activeSince = null;
    }
  };

  window.addEventListener('click', () => { _engagementState.clicks++; onActive(); }, { passive: true });
  window.addEventListener('keydown', () => { _engagementState.keystrokes++; onActive(); }, { passive: true });
  window.addEventListener('scroll', () => {
    const depth = Math.round(
      ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
    );
    _engagementState.scrollDepth = Math.max(_engagementState.scrollDepth, depth);
  }, { passive: true });
  window.addEventListener('blur', onIdle);
  window.addEventListener('focus', onActive);
};

/**
 * Get current engagement score (0-100)
 */
export const getEngagementScore = () => {
  const { clicks, scrollDepth, keystrokes, activeSeconds } = _engagementState;
  // Simple weighted score
  const clickScore = Math.min(clicks * 2, 30);
  const scrollScore = Math.min(scrollDepth * 0.3, 20);
  const keystrokeScore = Math.min(keystrokes * 0.5, 20);
  const timeScore = Math.min(activeSeconds * 0.5, 30);
  return Math.min(100, Math.round(clickScore + scrollScore + keystrokeScore + timeScore));
};

/**
 * Get raw engagement metrics
 */
export const getEngagementMetrics = () => ({
  ..._engagementState,
  score: getEngagementScore(),
  _activeSince: undefined,
  _activityTimer: undefined,
});

// ─── Funnel tracking ────────────────────────────────────────────

const _funnels = new Map(); // funnelName → { steps: string[], completed: Set }

/**
 * Define a funnel
 * @param {string} name - Funnel identifier
 * @param {string[]} steps - Ordered step names
 */
export const defineFunnel = (name, steps) => {
  _funnels.set(name, { steps, completed: new Set() });
};

/**
 * Track a funnel step
 * @param {string} funnelName
 * @param {string} stepName
 * @param {object} properties
 */
export const trackFunnelStep = (funnelName, stepName, properties = {}) => {
  const funnel = _funnels.get(funnelName);
  if (!funnel) {
    // Auto-create if not defined
    _funnels.set(funnelName, { steps: [stepName], completed: new Set([stepName]) });
    trackEvent('funnel_step', { funnel: funnelName, step: stepName, ...properties });
    return;
  }

  funnel.completed.add(stepName);
  const stepIndex = funnel.steps.indexOf(stepName);
  const totalSteps = funnel.steps.length;

  trackEvent('funnel_step', {
    funnel: funnelName,
    step: stepName,
    stepIndex,
    totalSteps,
    progress: totalSteps ? Math.round(((stepIndex + 1) / totalSteps) * 100) : 0,
    ...properties,
  });

  // Check if funnel completed
  if (funnel.completed.size === funnel.steps.length) {
    trackEvent('funnel_completed', {
      funnel: funnelName,
      totalSteps,
      ...properties,
    });
  }
};

/**
 * Get funnel state
 */
export const getFunnelState = (funnelName) => {
  const funnel = _funnels.get(funnelName);
  if (!funnel) return null;
  return {
    steps: funnel.steps,
    completed: [...funnel.completed],
    dropOffStep: funnel.steps.find((s) => !funnel.completed.has(s)) || null,
    completionRate: Math.round((funnel.completed.size / funnel.steps.length) * 100),
  };
};

// ─── Session ────────────────────────────────────────────────────

/**
 * Initialize session
 */
const initSession = () => {
  if (sessionId) return sessionId;

  sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  sessionStart = new Date().toISOString();

  // Store in sessionStorage for page refreshes
  try {
    const existing = sessionStorage.getItem('gabhan_session');
    if (existing) {
      const parsed = JSON.parse(existing);
      // Reuse if less than 30 minutes old
      if (Date.now() - new Date(parsed.start).getTime() < 30 * 60 * 1000) {
        sessionId = parsed.id;
        sessionStart = parsed.start;
        return sessionId;
      }
    }
    sessionStorage.setItem('gabhan_session', JSON.stringify({ id: sessionId, start: sessionStart }));
  } catch (e) {
    // sessionStorage not available
  }

  return sessionId;
};

/**
 * Get session duration in seconds
 */
export const getSessionDuration = () => {
  if (!sessionStart) return 0;
  return Math.round((Date.now() - new Date(sessionStart).getTime()) / 1000);
};

/**
 * Get session ID
 */
export const getSessionId = () => initSession();

// ─── Context helpers ────────────────────────────────────────────

/**
 * Get user context
 */
const getUserContext = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return {
      userId: user.id || user._id || null,
      organizationId: user.organizationId || user.organization || null,
      tier: user.subscription?.tier || 'free',
      role: user.role || 'user',
    };
  } catch {
    return { userId: null, organizationId: null, tier: 'free', role: 'user' };
  }
};

/**
 * Get device/browser context
 */
const getDeviceContext = () => {
  return {
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio || 1,
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    darkMode: window.matchMedia?.('(prefers-color-scheme: dark)').matches,
    online: navigator.onLine,
    connection: navigator.connection?.effectiveType || 'unknown',
  };
};

// ─── Opt-in / Opt-out ───────────────────────────────────────────

/**
 * Check if user has opted out of telemetry
 */
const isOptedOut = () => {
  try {
    return localStorage.getItem('gabhan_telemetry_optout') === 'true';
  } catch {
    return false;
  }
};

/**
 * Opt out of telemetry
 */
export const optOut = () => {
  try {
    localStorage.setItem('gabhan_telemetry_optout', 'true');
    eventQueue = []; // Clear pending events
    try { localStorage.removeItem(OFFLINE_STORAGE_KEY); } catch (_e) { /* noop */ }
  } catch (e) {
    console.warn('Could not save opt-out preference');
  }
};

/**
 * Opt back into telemetry
 */
export const optIn = () => {
  try {
    localStorage.removeItem('gabhan_telemetry_optout');
  } catch (e) {
    console.warn('Could not save opt-in preference');
  }
};

// ─── Sampling ───────────────────────────────────────────────────

/**
 * Determine whether an event should be sampled in
 */
const shouldSample = (eventName) => {
  const rate = SAMPLING_RATES[eventName] ?? SAMPLING_RATES._default;
  if (rate >= 1) return true;
  if (rate <= 0) return false;
  return Math.random() < rate;
};

/**
 * Set a custom sampling rate for an event type
 */
export const setSamplingRate = (eventName, rate) => {
  SAMPLING_RATES[eventName] = Math.max(0, Math.min(1, rate));
};

// ─── Throttle ───────────────────────────────────────────────────

/**
 * Check if an event type is being throttled
 */
const isThrottled = (eventName) => {
  const now = Date.now();
  let entry = _throttleMap.get(eventName);
  if (!entry || now - entry.windowStart > THROTTLE_WINDOW) {
    _throttleMap.set(eventName, { count: 1, windowStart: now });
    return false;
  }
  entry.count++;
  return entry.count > THROTTLE_MAX;
};

// ─── Offline persistence ────────────────────────────────────────

/**
 * Persist unsent events to localStorage for offline resilience
 */
const persistOffline = (events) => {
  try {
    const existing = JSON.parse(localStorage.getItem(OFFLINE_STORAGE_KEY) || '[]');
    const merged = [...existing, ...events].slice(-MAX_QUEUE_SIZE);
    localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(merged));
  } catch (_e) {
    // Storage unavailable — drop silently
  }
};

/**
 * Restore offline events back into queue
 */
const restoreOffline = () => {
  try {
    const stored = localStorage.getItem(OFFLINE_STORAGE_KEY);
    if (stored) {
      const events = JSON.parse(stored);
      if (events.length > 0) {
        eventQueue = [...events, ...eventQueue].slice(-MAX_QUEUE_SIZE);
        localStorage.removeItem(OFFLINE_STORAGE_KEY);
      }
    }
  } catch (_e) { /* noop */ }
};

// ─── Flush ──────────────────────────────────────────────────────

/**
 * Flush events to server
 */
const flushEvents = async () => {
  if (eventQueue.length === 0) return;

  const eventsToSend = [...eventQueue];
  eventQueue = [];

  try {
    const token = localStorage.getItem('access_token');
    const response = await fetch(TELEMETRY_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        events: eventsToSend,
        sessionId: initSession(),
        timestamp: new Date().toISOString(),
        engagement: getEngagementScore(),
      }),
      keepalive: true, // Ensure events are sent even on page unload
    });

    if (response.ok) {
      _flushRetryCount = 0;
    } else if (response.status >= 500) {
      throw new Error(`Server error ${response.status}`);
    }
  } catch (error) {
    _flushRetryCount++;
    if (_flushRetryCount <= MAX_FLUSH_RETRIES) {
      // Re-queue with cap
      eventQueue = [...eventsToSend, ...eventQueue].slice(-MAX_QUEUE_SIZE);
      // Exponential backoff for next flush
      const backoff = FLUSH_INTERVAL * Math.pow(2, _flushRetryCount - 1);
      scheduleFlush(backoff);
    } else {
      // Persist to localStorage for next session
      persistOffline(eventsToSend);
      _flushRetryCount = 0;
    }
  }
};

/**
 * Schedule flush with optional custom delay
 */
const scheduleFlush = (delay = FLUSH_INTERVAL) => {
  if (flushTimeout) clearTimeout(flushTimeout);

  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    flushEvents();
  }, delay);
};

// ─── Core tracking ──────────────────────────────────────────────

/**
 * Track an event
 */
export const trackEvent = (eventName, properties = {}) => {
  if (!TELEMETRY_ENABLED || isOptedOut()) return;

  // Throttle check
  if (isThrottled(eventName)) return;

  // Sampling check
  if (!shouldSample(eventName)) return;

  const event = {
    event: eventName,
    name: eventName,
    properties,
    timestamp: new Date().toISOString(),
    url: window.location.pathname,
    referrer: document.referrer || null,
    sessionDuration: getSessionDuration(),
    ...getUserContext(),
  };

  eventQueue.push(event);

  // Hard cap
  if (eventQueue.length > MAX_QUEUE_SIZE) {
    eventQueue = eventQueue.slice(-MAX_QUEUE_SIZE);
  }

  // Flush immediately if batch size reached
  if (eventQueue.length >= BATCH_SIZE) {
    flushEvents();
  } else {
    scheduleFlush();
  }

  // Also log to console in development
  if (import.meta.env.DEV) {
    console.log(`📊 [Telemetry] ${eventName}`, properties);
  }
};

/**
 * Track page view
 */
export const trackPageView = (pageName, properties = {}) => {
  trackEvent('page_view', {
    pageName,
    path: window.location.pathname,
    search: window.location.search,
    ...properties,
  });
};

/**
 * Track feature usage
 */
export const trackFeatureUsage = (featureName, action = 'used', properties = {}) => {
  trackEvent('feature_usage', {
    feature: featureName,
    action,
    ...properties,
  });
};

/**
 * Track error
 */
export const trackError = (error, context = {}) => {
  trackEvent('error', {
    message: error.message || String(error),
    stack: error.stack?.substring(0, 500), // Limit stack trace size
    ...context,
  });
};

/**
 * Track timing/performance
 */
export const trackTiming = (category, name, duration, properties = {}) => {
  trackEvent('timing', {
    category,
    name,
    duration,
    ...properties,
  });
};

/**
 * Track conversion event
 */
export const trackConversion = (conversionType, value = null, properties = {}) => {
  trackEvent('conversion', {
    type: conversionType,
    value,
    ...properties,
  });
};

/**
 * Identify user (call after login)
 */
export const identifyUser = (userId, traits = {}) => {
  trackEvent('identify', {
    userId,
    traits,
  });
};

/**
 * Add a session replay marker (for integration with Sentry/LogRocket/etc.)
 */
export const addReplayMarker = (label, data = {}) => {
  trackEvent('replay_marker', {
    label,
    ...data,
    sessionDuration: getSessionDuration(),
    engagement: getEngagementScore(),
  });
};

/**
 * Start timing a user action
 */
export const startTiming = (name) => {
  const startTime = performance.now();
  return {
    stop: (properties = {}) => {
      const duration = Math.round(performance.now() - startTime);
      trackTiming('user_action', name, duration, properties);
      return duration;
    },
  };
};

// ─── Queue visibility ───────────────────────────────────────────

/**
 * Get current queue size (for monitoring)
 */
export const getQueueSize = () => eventQueue.length;

/**
 * Get telemetry diagnostics
 */
export const getTelemetryDiagnostics = () => ({
  enabled: TELEMETRY_ENABLED,
  optedOut: isOptedOut(),
  queueSize: eventQueue.length,
  sessionId: initSession(),
  sessionDuration: getSessionDuration(),
  engagementScore: getEngagementScore(),
  flushRetries: _flushRetryCount,
  activeFunnels: [..._funnels.keys()],
});

/**
 * Force flush now (e.g., before navigation)
 */
export const forceFlush = () => flushEvents();

// ─── Pre-defined event helpers ──────────────────────────────────

export const events = {
  // Onboarding
  onboardingStarted: () => trackEvent('onboarding_started'),
  onboardingCompleted: (data) => trackConversion('onboarding_complete', null, data),
  onboardingSkipped: (step) => trackEvent('onboarding_skipped', { step }),

  // Authentication
  signUp: (method = 'email') => trackConversion('sign_up', null, { method }),
  login: (method = 'email') => trackEvent('login', { method }),
  logout: () => {
    trackEvent('logout', { sessionDuration: getSessionDuration(), engagement: getEngagementScore() });
    flushEvents();
  },

  // Subscription
  viewedPricing: () => trackEvent('viewed_pricing'),
  startedTrial: (plan) => trackConversion('trial_started', null, { plan }),
  subscribed: (plan, value) => trackConversion('subscription', value, { plan }),
  upgraded: (fromPlan, toPlan, value) => trackConversion('upgrade', value, { fromPlan, toPlan }),
  cancelled: (plan, reason) => trackEvent('subscription_cancelled', { plan, reason }),

  // Features
  productCreated: () => trackFeatureUsage('products', 'created'),
  orderCreated: (value) => trackFeatureUsage('orders', 'created', { value }),
  reportGenerated: (type) => trackFeatureUsage('reports', 'generated', { type }),
  integrationConnected: (platform) => trackFeatureUsage('integrations', 'connected', { platform }),

  // Engagement
  searchPerformed: (query, resultCount) =>
    trackEvent('search', { query: query.substring(0, 50), resultCount }),
  exportData: (format, recordCount) =>
    trackFeatureUsage('export', 'completed', { format, recordCount }),

  // Errors & Support
  errorEncountered: (error, context) => trackError(error, context),
  supportRequested: (type) => trackEvent('support_requested', { type }),
  feedbackSubmitted: (rating, category) => trackEvent('feedback', { rating, category }),

  // A/B experiments
  experimentExposure: (experimentId, variant) =>
    trackEvent('experiment_exposure', { experimentId, variant }),
  experimentConversion: (experimentId, variant, value) =>
    trackConversion('experiment_conversion', value, { experimentId, variant }),
};

// ─── Lifecycle handlers ─────────────────────────────────────────

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushEvents();
    }
  });

  // Also flush before page unload
  window.addEventListener('beforeunload', () => {
    flushEvents();
  });

  // Restore offline events when back online
  window.addEventListener('online', () => {
    restoreOffline();
    if (eventQueue.length > 0) {
      scheduleFlush(2000); // slight delay after reconnect
    }
  });
}

// Initialize session + engagement on load
initSession();
restoreOffline();
_initEngagement();

export default {
  trackEvent,
  trackPageView,
  trackFeatureUsage,
  trackError,
  trackTiming,
  trackConversion,
  identifyUser,
  startTiming,
  addReplayMarker,
  optOut,
  optIn,
  events,
  getDeviceContext,
  getEngagementScore,
  getEngagementMetrics,
  getSessionDuration,
  getSessionId,
  getQueueSize,
  getTelemetryDiagnostics,
  forceFlush,
  defineFunnel,
  trackFunnelStep,
  getFunnelState,
  setSamplingRate,
};
